/**
 * Rules on a board as a whole and on the position plan's orientation and routes:
 * - R-BOARD-01: unused openings sealed (blanking plates, RGIE art. 49) — what the user saw on site;
 * - R-CALC-06: phase balance of a three-phase board, from the stated loads;
 * - R-SEL-01 / R-SEL-02: selectivity of RCDs in series, and of the circuits with the supply;
 * - R-PLAN-02 / R-PLAN-03: north arrow, board location and main cable routes (RGIE POS-02/03).
 * See docs/decisions/0014-board-plan-signoff-clients.md.
 */
import type { Element, Id, Project, Sheet } from '@elec/domain';
import { boardRootSheet, pointKind, type Analysis } from '@elec/analysis';
import { improvableUnbalance } from '@elec/calc';
import { elementBounds, getSymbol } from '@elec/symbols';
import { fmt, name, rule, sensitivityMa, type L, type Rule } from './core.ts';

/** Boards of the project: the single-line sheets a board starts on (continuations excluded). */
export function boardSheets(project: Project, a: Analysis): Sheet[] {
  const roots = new Set<Id>();
  for (const c of a.circuits) roots.add(boardRootSheet(project, a, c.sheet.id));
  return Object.values(project.sheets)
    .filter((s) => s.kind === 'single-line' && roots.has(s.id))
    .sort((x, y) => x.order - y.order);
}

const quoted = (s: Sheet): L => ({
  fr: `« ${s.name} »`,
  en: `“${s.name}”`,
  nl: `“${s.name}”`,
  de: `„${s.name}“`,
});

export const BOARD_RULES: Record<string, Rule> = {
  'R-BOARD-01': rule(
    {
      id: 'R-BOARD-01',
      severity: 'info',
      title: {
        fr: 'Ouvertures du tableau obturées',
        en: 'Board openings sealed',
        nl: 'Openingen van het bord afgesloten',
        de: 'Öffnungen des Verteilers verschlossen',
      },
      why: {
        fr: 'Les emplacements libres du tableau doivent être fermés par des obturateurs : sans eux, des parties sous tension restent accessibles (RGIE art. 49 ; remarque fréquente des organismes).',
        en: 'Free spaces in the board must be closed with blanking plates: without them, live parts can be touched (RGIE art. 49; a frequent remark of inspection bodies).',
        nl: 'Vrije plaatsen in het bord moeten afgesloten zijn met afdekplaatjes: zonder ze blijven delen onder spanning bereikbaar (AREI art. 49; een frequente opmerking van de keuringsorganismen).',
        de: 'Freie Plätze im Verteiler müssen mit Abdeckungen verschlossen sein: ohne sie bleiben spannungsführende Teile berührbar (AREI Art. 49; häufige Bemerkung der Kontrollstellen).',
      },
      fix: {
        fr: 'Posez des obturateurs sur les emplacements libres, puis marquez-les posés (bouton ci-dessous ou relevé sur site).',
        en: 'Fit blanking plates on the free spaces, then mark them fitted (button below, or the site survey).',
        nl: 'Plaats afdekplaatjes op de vrije plaatsen en duid ze aan als geplaatst (knop hieronder of de opname ter plaatse).',
        de: 'Setzen Sie Abdeckungen auf die freien Plätze und kennzeichnen Sie sie dann als gesetzt (Schaltfläche unten oder Aufnahme vor Ort).',
      },
      verified: false,
    },
    ({ project, a }) =>
      boardSheets(project, a).flatMap((s) => {
        if (s.blankingPlates === 'ok') return [];
        const q = quoted(s);
        return [
          s.blankingPlates === 'missing'
            ? {
                severity: 'warning' as const,
                targets: [],
                sheetId: s.id,
                message: {
                  fr: `Tableau ${q.fr} : emplacements libres non obturés`,
                  en: `Board ${q.en}: free spaces not sealed`,
                  nl: `Bord ${q.nl}: vrije plaatsen niet afgesloten`,
                  de: `Verteiler ${q.de}: freie Plätze nicht verschlossen`,
                },
              }
            : {
                targets: [],
                sheetId: s.id,
                message: {
                  fr: `Tableau ${q.fr} : obturateurs sur les emplacements libres ? (à vérifier sur place)`,
                  en: `Board ${q.en}: blanking plates on the free spaces? (to check on site)`,
                  nl: `Bord ${q.nl}: afdekplaatjes op de vrije plaatsen? (ter plaatse na te kijken)`,
                  de: `Verteiler ${q.de}: Abdeckungen auf den freien Plätzen? (vor Ort zu prüfen)`,
                },
              },
        ];
      }),
  ),
  'R-CALC-06': rule(
    {
      id: 'R-CALC-06',
      severity: 'warning',
      title: {
        fr: 'Phases déséquilibrées',
        en: 'Unbalanced phases',
        nl: 'Fasen niet in evenwicht',
        de: 'Phasen unsymmetrisch belastet',
      },
      why: {
        fr: 'Sur un tableau triphasé, les charges doivent être réparties sur L1, L2 et L3 : une phase trop chargée chauffe, fait déclencher la protection de branchement et charge le neutre (calcul sur les puissances indiquées).',
        en: 'On a three-phase board, loads should be spread over L1, L2 and L3: an overloaded phase heats up, trips the supply protection and loads the neutral (computed from the stated powers).',
        nl: 'Op een driefasig bord moeten de belastingen over L1, L2 en L3 verdeeld zijn: een te zwaar belaste fase warmt op, doet de aansluitbeveiliging uitschakelen en belast de nulgeleider (berekend uit de opgegeven vermogens).',
        de: 'In einem Drehstromverteiler müssen die Lasten auf L1, L2 und L3 verteilt sein: eine zu stark belastete Phase erwärmt sich, löst den Hausanschlussschutz aus und belastet den Neutralleiter (berechnet aus den angegebenen Leistungen).',
      },
      fix: {
        fr: 'Répartissez les circuits monophasés sur les phases (bouton ci-dessous ou propriété « Phase » du disjoncteur), puis mettez le multifilaire à jour.',
        en: 'Spread the single-phase circuits over the phases (button below, or the breaker’s “Phase” property), then update the multi-line diagram.',
        nl: 'Verdeel de eenfasige kringen over de fasen (knop hieronder of de eigenschap “Fase” van de automaat) en werk het meerdraadsschema bij.',
        de: 'Verteilen Sie die einphasigen Stromkreise auf die Phasen (Schaltfläche unten oder Eigenschaft „Phase“ des LS), und aktualisieren Sie dann das Mehrliniendiagramm.',
      },
      verified: false,
    },
    ({ project, a }) =>
      boardSheets(project, a).flatMap((s) => {
        const r = improvableUnbalance(project, a, s.id);
        if (!r) return [];
        const l = r.loads;
        const u = { deltaA: r.deltaA };
        const per = (en: boolean) =>
          (['L1', 'L2', 'L3'] as const).map((p) => `${p} ${fmt(l.amps[p], en)} A`).join(' · ');
        const q = quoted(s);
        return [
          {
            targets: [],
            sheetId: s.id,
            message: {
              fr: `Tableau ${q.fr} : ${per(false)} (écart ${fmt(u.deltaA)} A)`,
              en: `Board ${q.en}: ${per(true)} (difference ${fmt(u.deltaA, true)} A)`,
              nl: `Bord ${q.nl}: ${per(false)} (verschil ${fmt(u.deltaA)} A)`,
              de: `Verteiler ${q.de}: ${per(false)} (Abweichung ${fmt(u.deltaA)} A)`,
            },
          },
        ];
      }),
  ),
  'R-SEL-01': rule(
    {
      id: 'R-SEL-01',
      severity: 'warning',
      title: {
        fr: 'Différentiels en série non sélectifs',
        en: 'RCDs in series not selective',
        nl: 'Differentiëlen in serie niet selectief',
        de: 'FI-Schutzschalter in Reihe nicht selektiv',
      },
      why: {
        fr: 'Un différentiel en amont d’un autre ne reste fermé lors d’un défaut en aval que s’il est au moins 3 fois moins sensible (et de préférence retardé, type S) : sinon un défaut sur un circuit coupe aussi tous les autres (IEC 60364-5-53).',
        en: 'An RCD upstream of another only stays closed on a fault downstream when it is at least 3 times less sensitive (and preferably time-delayed, type S): otherwise a fault on one circuit cuts all the others too (IEC 60364-5-53).',
        nl: 'Een differentieel stroomopwaarts van een ander blijft bij een fout stroomafwaarts enkel gesloten als het minstens 3 keer minder gevoelig is (en bij voorkeur vertraagd, type S): anders schakelt een fout op één kring ook alle andere af (IEC 60364-5-53).',
        de: 'Ein FI vor einem anderen bleibt bei einem nachgeschalteten Fehler nur eingeschaltet, wenn er mindestens 3-mal weniger empfindlich ist (und vorzugsweise verzögert, Typ S): sonst schaltet ein Fehler in einem Stromkreis auch alle anderen ab (IEC 60364-5-53).',
      },
      fix: {
        fr: 'Prenez en amont un différentiel au moins 3 fois moins sensible (300 mA au-dessus de 30 mA), de préférence de type S, ou raccordez le circuit directement sous le différentiel général.',
        en: 'Use an upstream RCD at least 3 times less sensitive (300 mA above 30 mA), preferably type S, or connect the circuit directly under the main RCD.',
        nl: 'Gebruik stroomopwaarts een differentieel dat minstens 3 keer minder gevoelig is (300 mA boven 30 mA), bij voorkeur type S, of sluit de kring rechtstreeks aan onder de hoofddifferentieel.',
        de: 'Verwenden Sie vorgeschaltet einen mindestens 3-mal weniger empfindlichen FI (300 mA vor 30 mA), vorzugsweise Typ S, oder schließen Sie den Stromkreis direkt hinter dem Haupt-FI an.',
      },
      verified: false,
    },
    ({ a }) => {
      const seen = new Set<string>();
      const out = [];
      for (const c of a.circuits)
        for (let i = 0; i + 1 < c.rcds.length; i++) {
          const down = c.rcds[i]!;
          const up = c.rcds[i + 1]!;
          const key = `${up.id}>${down.id}`;
          if (seen.has(key)) continue;
          seen.add(key);
          const sd = sensitivityMa(down);
          const su = sensitivityMa(up);
          if (!Number.isFinite(sd) || !Number.isFinite(su) || su >= 3 * sd) continue;
          out.push({
            targets: [up.id, down.id],
            sheetId: up.sheetId,
            message: {
              fr: `${up.tag} (${su} mA) en amont de ${down.tag} (${sd} mA) : pas de sélectivité`,
              en: `${up.tag} (${su} mA) upstream of ${down.tag} (${sd} mA): not selective`,
              nl: `${up.tag} (${su} mA) stroomopwaarts van ${down.tag} (${sd} mA): niet selectief`,
              de: `${up.tag} (${su} mA) vor ${down.tag} (${sd} mA): keine Selektivität`,
            },
          });
        }
      return out;
    },
  ),
  'R-SEL-02': rule(
    {
      id: 'R-SEL-02',
      severity: 'warning',
      title: {
        fr: 'Circuit non sélectif avec le branchement',
        en: 'Circuit not selective with the supply',
        nl: 'Kring niet selectief met de aansluiting',
        de: 'Stromkreis nicht selektiv zum Hausanschluss',
      },
      why: {
        fr: 'Une protection de circuit dont le calibre atteint celui du raccordement (disjoncteur de branchement du gestionnaire de réseau) ne déclenche pas avant lui : une surcharge ou un court-circuit coupe toute l’installation.',
        en: 'A circuit protection rated as high as the connection (the network operator’s supply breaker) does not trip before it: an overload or short circuit cuts the whole installation.',
        nl: 'Een kringbeveiliging met een stroomsterkte gelijk aan die van de aansluiting (de aansluitautomaat van de netbeheerder) schakelt niet vóór die af: een overbelasting of kortsluiting schakelt de hele installatie af.',
        de: 'Ein Stromkreisschutz, dessen Bemessungsstrom den des Anschlusses (Anschlusssicherung des Netzbetreibers) erreicht, löst nicht vor diesem aus: eine Überlast oder ein Kurzschluss schaltet die ganze Installation ab.',
      },
      fix: {
        fr: 'Choisissez un calibre de circuit nettement inférieur au raccordement (au moins un calibre en dessous), ou demandez un raccordement plus puissant.',
        en: 'Use a circuit rating clearly below the connection (at least one size below), or ask for a bigger connection.',
        nl: 'Kies een kringstroomsterkte duidelijk onder die van de aansluiting (minstens één kaliber lager), of vraag een zwaardere aansluiting aan.',
        de: 'Wählen Sie einen Bemessungsstrom des Stromkreises deutlich unter dem des Anschlusses (mindestens eine Stufe darunter), oder beantragen Sie einen stärkeren Anschluss.',
      },
      verified: false,
    },
    ({ project, a }) => {
      const supplyOf = new Map<Id, Element | undefined>();
      const supply = (sheetId: Id) => {
        if (!supplyOf.has(sheetId))
          supplyOf.set(
            sheetId,
            Object.values(project.elements).find((e) => e.sheetId === sheetId && e.symbolId === 'iec.supply'),
          );
        return supplyOf.get(sheetId);
      };
      return a.circuits.flatMap((c) => {
        const s = supply(boardRootSheet(project, a, c.sheet.id));
        const conn = Number(s?.props.ratingA ?? (s ? 40 : NaN));
        if (!s || !Number.isFinite(conn) || !c.ratingA || c.ratingA < conn) return [];
        return [
          {
            targets: [c.device.id],
            sheetId: c.sheet.id,
            message: {
              fr: `Circuit ${name(c)} : ${c.ratingA} A pour un raccordement de ${conn} A`,
              en: `Circuit ${name(c)}: ${c.ratingA} A on a ${conn} A connection`,
              nl: `Kring ${name(c)}: ${c.ratingA} A op een aansluiting van ${conn} A`,
              de: `Stromkreis ${name(c)}: ${c.ratingA} A an einem Anschluss von ${conn} A`,
            },
          },
        ];
      });
    },
  ),
};

const plans = (p: Project) =>
  Object.values(p.sheets)
    .filter((s) => s.kind === 'position-plan')
    .sort((a, b) => a.order - b.order);
const on = (p: Project, sheetId: Id) => Object.values(p.elements).filter((e) => e.sheetId === sheetId);

/** Is a wire of the plan attached to the board, or does it start or end on its outline? */
export function hasCableRoute(project: Project, board: Element): boolean {
  const b = elementBounds(getSymbol(board.symbolId)!, board);
  const near = (x: number, y: number) =>
    x >= b.minX - 2 && x <= b.maxX + 2 && y >= b.minY - 2 && y <= b.maxY + 2;
  return Object.values(project.wires).some(
    (w) =>
      w.sheetId === board.sheetId &&
      [w.from, w.to].some((e) => (e.kind === 'port' ? e.elementId === board.id : near(e.x, e.y))),
  );
}

export const PLAN_RULES: Record<string, Rule> = {
  'R-PLAN-02': rule(
    {
      id: 'R-PLAN-02',
      severity: 'info',
      title: {
        fr: 'Orientation du plan',
        en: 'Plan orientation',
        nl: 'Oriëntatie van het plan',
        de: 'Ausrichtung des Plans',
      },
      why: {
        fr: 'Le plan de position indique l’orientation (le nord), pour situer les pièces et les façades.',
        en: 'The position plan shows its orientation (north), to locate rooms and façades.',
        nl: 'Het situatieschema toont de oriëntatie (het noorden), om de ruimtes en gevels te situeren.',
        de: 'Der Lageplan zeigt die Ausrichtung (Norden), um Räume und Fassaden zu verorten.',
      },
      fix: {
        fr: 'Ajoutez une flèche du nord (bouton ci-dessous) et réglez son angle dans ses propriétés.',
        en: 'Add a north arrow (button below) and set its angle in its properties.',
        nl: 'Voeg een noordpijl toe (knop hieronder) en stel de hoek in bij de eigenschappen.',
        de: 'Fügen Sie einen Nordpfeil hinzu (Schaltfläche unten) und stellen Sie seinen Winkel in den Eigenschaften ein.',
      },
      verified: false,
    },
    ({ project }) =>
      plans(project)
        .filter((s) => on(project, s.id).some((e) => pointKind(e)))
        .filter((s) => !on(project, s.id).some((e) => e.symbolId === 'plan.north'))
        .map((s) => {
          const q = quoted(s);
          return {
            targets: [],
            sheetId: s.id,
            message: {
              fr: `${q.fr} : pas de flèche du nord`,
              en: `${q.en}: no north arrow`,
              nl: `${q.nl}: geen noordpijl`,
              de: `${q.de}: kein Nordpfeil`,
            },
          };
        }),
  ),
  'R-PLAN-03': rule(
    {
      id: 'R-PLAN-03',
      severity: 'info',
      title: {
        fr: 'Tableau et chemins de câbles au plan',
        en: 'Board and cable routes on the plan',
        nl: 'Bord en kabelwegen op het plan',
        de: 'Verteiler und Kabelwege im Plan',
      },
      why: {
        fr: 'Le plan de position situe le ou les tableaux et le tracé des principales canalisations.',
        en: 'The position plan locates the board(s) and the main cable routes.',
        nl: 'Het situatieschema situeert het bord of de borden en het tracé van de belangrijkste leidingen.',
        de: 'Der Lageplan zeigt den oder die Verteiler und den Verlauf der wichtigsten Leitungen.',
      },
      fix: {
        fr: 'Placez le tableau sur le plan, puis tracez les chemins de câbles (bouton ci-dessous, à corriger selon le tracé réel).',
        en: 'Place the board on the plan, then draw the cable routes (button below; correct them to the real routes).',
        nl: 'Plaats het bord op het plan en teken de kabelwegen (knop hieronder; pas ze aan het werkelijke tracé aan).',
        de: 'Setzen Sie den Verteiler in den Plan und zeichnen Sie dann die Kabelwege ein (Schaltfläche unten; an den tatsächlichen Verlauf anpassen).',
      },
      verified: false,
    },
    ({ project }) => {
      const withPoints = plans(project).filter((s) => on(project, s.id).some((e) => pointKind(e)));
      if (!withPoints.length) return [];
      const boards = withPoints.flatMap((s) => on(project, s.id).filter((e) => e.symbolId === 'iec.board'));
      if (!boards.length)
        return [
          {
            targets: [],
            sheetId: withPoints[0]!.id,
            message: {
              fr: 'Plan de position : emplacement du tableau non indiqué',
              en: 'Position plan: board location not shown',
              nl: 'Situatieschema: plaats van het bord niet aangeduid',
              de: 'Lageplan: Standort des Verteilers nicht angegeben',
            },
          },
        ];
      return boards
        .filter((b) => !hasCableRoute(project, b))
        .map((b) => ({
          targets: [b.id],
          sheetId: b.sheetId,
          message: {
            fr: `Tableau ${b.tag || ''} : chemins de câbles non tracés`.replace('  ', ' '),
            en: `Board ${b.tag || ''}: cable routes not drawn`.replace('  ', ' '),
            nl: `Bord ${b.tag || ''}: kabelwegen niet getekend`.replace('  ', ' '),
            de: `Verteiler ${b.tag || ''}: Kabelwege nicht eingezeichnet`.replace('  ', ' '),
          },
        }));
    },
  ),
};
